<i18n>
    [
        "product__prepurchase--tagComponentText",
    ]
</i18n>

<template>
    <div class="c-image-viewer" :style="cssVars">
        <div v-if="isMounted" class="c-image-viewer__container">
            <Badge
                v-if="isBuyMode"
                class="c-image-viewer__badge"
                :badgeText="$t('product__prepurchase--tagComponentText')"
                icon="account--promo"
                :bkgColor="BADGE_COLORS.sunshine"
                color="black"
            />
            <ul
                v-if="$mediaQueries.isLargish"
                class="c-image-viewer__image-grid o-row-full o-row-full--no-gap"
            >
                <li
                    v-for="(image, index) in productImages"
                    :key="`imageViewer-${index}`"
                    class="c-image-viewer__slide o-extra-small--6"
                >
                    <ImageViewerImage
                        :productName="productName"
                        :productIndex="index"
                        :productImages="productImages"
                        :imagesFullWidth="imagesFullWidth"
                        :productImage="image"
                        :choiceId="choiceId"
                    />
                </li>
            </ul>
            <Carousel
                v-else
                class="c-image-viewer__image-carousal"
                :name="`${productName}-image-viewer`"
                :carouselItems="productImages"
                :translucentButtons="true"
                :lightButtons="true"
                :resetOnResize="true"
                :noContainerMargin="true"
                :noSlidePadding="true"
                :mobileSlideSize="1"
                :mediumSlideSize="1"
                :analyticsType="analyticsType"
            >
                <template
                    v-for="(image, index) in productImages"
                    :key="`zoom-launcher-${index}`"
                    #[`carouselItem-${index}`]
                >
                    <ImageViewerImage
                        :productName="productName"
                        :productIndex="index"
                        :productImages="productImages"
                        :productImage="image"
                        :imagesFullWidth="imagesFullWidth"
                        :choiceId="choiceId"
                    />
                </template>
            </Carousel>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';

import { BADGE_COLORS } from '~coreModules/core/js/badge-constants';

import Carousel from '~coreModules/core/components/ui/Carousel.vue';
import ImageViewerImage from '~coreRoutes/product/components/ImageViewerImage.vue';
import Badge from '~coreModules/core/components/ui/Badge.vue';

const props = defineProps({
    productImages: {
        type: Array,
        required: true,
    },
    productName: {
        type: String,
        required: true,
    },
    imagesFullWidth: {
        type: Boolean,
        default: true,
    },
    choiceId: {
        type: String,
        required: true,
    },
    analyticsType: {
        type: String,
        default: null,
    },
    isBuyMode: {
        type: Boolean,
        default: false,
    },
});

const isMounted = ref(false);

const cssVars = computed(() => {
    // 4:3 aspect ratio means we need 75% per row of 2 images
    const imageCount = props.productImages?.length || 0;
    const padding = (Math.floor(imageCount / 2) + (imageCount % 2)) * 75;
    return {
        '--placeholder-padding-top': `${padding}%`,
    };
});

onMounted(() => {
    isMounted.value = true;
});

</script>

<style lang="scss">
.c-image-viewer {
    position: relative;
    width: 100%;
    height: 100%;
    margin-bottom: $nu-spacer-2;
    overflow: hidden;

    @include breakpoint(large) {
        position: sticky;
        height: auto;
        margin-bottom: 0;
        background-color: unset;
    }

    &::before {
        position: relative;
        display: block;
        content: "";
        width: 100%;
        padding-top: 130%; /* 489/375 aspect ratio */
        opacity: 0;

        @include breakpoint(large) {
            content: none;
        }
    }

    &__container {
        position: absolute;
        top: 0;
        width: 100%;

        @include breakpoint(large) {
            position: sticky;
        }
    }

    &__link,
    &__launcher-link,
    .c-lazy-image__fade-in {
        width: 100%;
        display:block;
    }

    &__badge {
        position: absolute;
        bottom: $nu-spacer-2;
        left: $nu-spacer-2;
        pointer-events: none;
        z-index: map-get($zindex, carousel-badge);

        @include breakpoint(medium) {
            display: none;
        }
    }
}
</style>
